import React, { useEffect, useMemo } from 'react';
import styled, { css } from 'styled-components';
import {
  FiMapPin,
  FiShield,
  FiEye,
  FiDatabase,
  FiPieChart,
  FiTruck,
  FiLayers,
  FiGlobe,
  FiZap,
  FiAward,
  FiTarget,
  FiGrid,
  FiMonitor,
  FiBox,
  FiCheckSquare,
  FiSettings,
  FiRefreshCw,
  FiCode,
  FiSend,
  FiUsers,
  FiBriefcase,
  FiUserPlus,
} from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { useTokenization } from '../../contexts/TokenizationContext';
import { useTokenIssuer } from '../../contexts/TokenIssuerContext';
import { motion } from 'framer-motion';

const DashboardContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 2rem;
  background: linear-gradient(135deg, #0f0f23 0%, #1a1a3e 100%);
  min-height: 100vh;
`;

const HeroSection = styled.div`
  background: rgba(255, 255, 255, 0.02);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 4rem 3rem;
  margin-bottom: 3rem;
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #4facfe, #00f2fe, #4facfe);
    animation: shimmer 3s infinite linear;
  }

  @keyframes shimmer {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
  }
`;

const Title = styled.h1`
  font-size: 3.5rem;
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 1.5rem;
  font-weight: 800;
  letter-spacing: -1px;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.3rem;
  color: #a0a0a0;
  max-width: 700px;
  margin: 0 auto 2rem;
  line-height: 1.7;
`;

const HeroFeatures = styled.div`
  display: flex;
  justify-content: center;
  gap: 3rem;
  flex-wrap: wrap;
  margin-top: 2rem;
`;

const HeroFeature = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: #4facfe;
  font-weight: 600;
  font-size: 1.1rem;

  .icon {
    font-size: 1.3rem;
  }
`;

const CTAButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 2rem;
  background: linear-gradient(135deg, #4facfe, #00f2fe);
  color: #fff;
  text-decoration: none;
  border-radius: 12px;
  font-weight: 700;
  font-size: 1.1rem;
  margin-top: 2rem;
  transition: all 0.3s ease;
  box-shadow: 0 10px 30px rgba(79, 172, 254, 0.3);

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 15px 40px rgba(79, 172, 254, 0.4);
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
  margin-bottom: 3rem;
`;

const StatCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.02);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 15px;
  padding: 2rem;
  text-align: center;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    border-color: #4facfe;
    box-shadow: 0 20px 40px rgba(79, 172, 254, 0.1);
  }

  .stat-icon {
    font-size: 2.5rem;
    color: #4facfe;
    margin-bottom: 1rem;
  }

  .stat-value {
    font-size: 2.5rem;
    font-weight: 700;
    color: #4facfe;
    margin-bottom: 0.5rem;
  }

  .stat-label {
    color: #a0a0a0;
    margin-bottom: 0.5rem;
  }

  .stat-meta {
    font-size: 0.85rem;
    color: #64748b;
    line-height: 1.4;
    margin-top: 0.25rem;
  }
`;

const CategoriesSection = styled.section`
  margin-bottom: 3rem;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  margin-bottom: 2rem;
  color: #fff;
  display: flex;
  align-items: center;
  gap: 1rem;

  .icon {
    color: #4facfe;
  }
`;

const CategoriesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-bottom: 3rem;
`;

const CategoryCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.02);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 15px;
  padding: 2rem;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &:hover {
    transform: translateY(-3px);
    border-color: ${props => props.color || '#4facfe'};
    box-shadow: 0 15px 30px ${props => props.color || '#4facfe'}20;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: ${props => props.color || '#4facfe'};
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover::before {
    opacity: 1;
  }

  .category-header {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 1rem;
  }

  .category-icon {
    font-size: 2.5rem;
  }

  .category-name {
    font-size: 1.3rem;
    font-weight: 700;
    color: #fff;
  }

  .category-description {
    color: #a0a0a0;
    line-height: 1.5;
    margin-bottom: 1.5rem;
  }

  .category-stats {
    display: flex;
    justify-content: space-between;
    font-size: 0.9rem;
  }

  .category-stat {
    text-align: center;
    
    .value {
      font-weight: 600;
      color: #4facfe;
      display: block;
    }
    
    .label {
      color: #a0a0a0;
    }
  }
`;

const QuickActionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
`;

const ToolsSection = styled.section`
  margin-bottom: 3rem;
`;

const toolsAndOperationsLinks = [
  {
    to: '/api-management',
    title: 'API',
    description: 'Integration overview, endpoints, and developer-facing API resources for the platform.',
    Icon: FiCode,
  },
  {
    to: '/asset-classes',
    title: 'Asset classes',
    description: 'Configure asset classes, metadata, and deployment for tokenization programs.',
    Icon: FiDatabase,
  },
  {
    to: '/tokenization?modal=vehicle',
    title: 'Vehicle NFTs',
    description: 'Launch the vehicle tokenization flow with structured metadata and ownership records.',
    Icon: FiTruck,
  },
  {
    to: '/asset-tracking',
    title: 'Asset Tracking',
    description: 'Real-time blockchain tracking of physical assets and custody locations.',
    Icon: FiMapPin,
  },
  {
    to: '/transparency',
    title: 'Transparency Hub',
    description: 'Proof of reserves, audit reports, and compliance documentation.',
    Icon: FiEye,
  },
  {
    to: '/inventory-management',
    title: 'Inventory',
    description: 'Manage vaulted inventory positions and reconciliation.',
    Icon: FiDatabase,
  },
  {
    to: '/custodial-operations',
    title: 'Custodial',
    description: 'Custody workflows, settlements, and operational controls.',
    Icon: FiShield,
  },
  {
    to: '/real-time-monitoring',
    title: 'Monitoring',
    description: 'Live platform and chain health, alerts, and throughput.',
    Icon: FiMonitor,
  },
  {
    to: '/blockchain-explorer',
    title: 'Explorer',
    description: 'Search transactions, contracts, and on-chain activity.',
    Icon: FiLayers,
  },
  {
    to: '/warehouse-operations',
    title: 'Warehouse',
    description: 'Warehouse logistics, receipts, and physical asset handling.',
    Icon: FiBox,
  },
  {
    to: '/quality-management',
    title: 'Quality',
    description: 'Quality assurance checks and attestations for tokenized assets.',
    Icon: FiCheckSquare,
  },
  {
    to: '/compliance-center',
    title: 'Compliance',
    description: 'Policies, reviews, and regulatory reporting tools.',
    Icon: FiSettings,
  },
  {
    to: '/conversion-center',
    title: 'Conversion',
    description: 'Convert between representations, units, or settlement rails.',
    Icon: FiRefreshCw,
  },
];

/** Matches CoreTokenizer Assets section ActionCardLink / actionCardSurface */
const actionCardSurface = css`
  background: rgba(255, 255, 255, 0.02);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 2.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  color: inherit;
  text-align: left;

  &:hover {
    transform: translateY(-5px);
    border-color: #4facfe;
    box-shadow: 0 20px 40px rgba(79, 172, 254, 0.15);
  }

  .icon {
    font-size: 3rem;
    color: #4facfe;
    margin-bottom: 1.5rem;
  }

  .title {
    font-size: 1.5rem;
    font-weight: 700;
    color: #fff;
    margin-bottom: 1rem;
  }

  .description {
    color: #a0a0a0;
    line-height: 1.6;
    margin-bottom: 1.5rem;
  }

  .features {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .feature-tag {
    background: rgba(79, 172, 254, 0.1);
    border: 1px solid rgba(79, 172, 254, 0.3);
    border-radius: 12px;
    padding: 0.3rem 0.7rem;
    font-size: 0.8rem;
    color: #4facfe;
  }
`;

const MotionLink = motion(Link);

const AdminDashboardActionLink = styled(MotionLink)`
  ${actionCardSurface}
  text-decoration: none;
  display: block;
`;

const AdminDashboardCardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 2rem;
  margin-bottom: 2.5rem;
`;

const QuickActionCard = styled(Link)`
  background: rgba(255, 255, 255, 0.02);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 15px;
  padding: 2rem;
  text-decoration: none;
  color: inherit;
  text-align: center;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;

  &:hover {
    transform: translateY(-5px);
    border-color: #4facfe;
    box-shadow: 0 20px 40px rgba(79, 172, 254, 0.1);
  }

  .action-icon {
    font-size: 3rem;
    color: #4facfe;
  }

  .action-title {
    font-size: 1.3rem;
    margin-bottom: 0.5rem;
    color: #fff;
    font-weight: 600;
  }

  .action-description {
    color: #a0a0a0;
    font-size: 0.9rem;
    line-height: 1.5;
  }
`;

function formatUsdCompact(value) {
  const n = Number(value) || 0;
  if (n >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(2)}B`;
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
  if (n > 0) return `$${Math.round(n).toLocaleString()}`;
  return '$0';
}

function Dashboard() {
  const { statistics, categories, assets, loading, loadAssets } = useTokenization();
  const { isAdmin, isLegalReviewer, currentIssuer } = useTokenIssuer();

  const toolsAndOperationsItems = useMemo(() => {
    if (!isAdmin) return toolsAndOperationsLinks;
    const next = [...toolsAndOperationsLinks];
    const idx = next.findIndex((l) => l.to === '/asset-classes');
    const assetsCard = {
      to: '/admin/assets',
      title: 'Assets',
      description:
        'Full registry: filter by tokenizer, sector, legal reviewer, status, and open contracts, mints, and documents.',
      Icon: FiDatabase,
    };
    if (idx >= 0) next.splice(idx + 1, 0, assetsCard);
    else next.unshift(assetsCard);
    return next;
  }, [isAdmin]);

  useEffect(() => {
    void loadAssets();
    // Intentionally align with TokenizationProvider: refresh when the signed-in user changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIssuer?.id]);

  const categoryStats = useMemo(() => {
    const out = {};
    for (const category of Object.values(categories)) {
      const id = category?.id;
      if (!id) continue;
      const list = assets.filter((asset) => asset.category === id);
      const tokenized = list.filter(
        (a) =>
          a.status === 'Minted' ||
          a.status === 'minted' ||
          String(a.status || '').toLowerCase() === 'minted' ||
          Boolean(a.tokenizationId)
      ).length;
      out[id] = {
        assets: list.length,
        value: list.reduce((sum, a) => sum + (Number(a.valuationUSD) || 0), 0),
        tokenized
      };
    }
    return out;
  }, [assets, categories]);

  return (
    <DashboardContainer>
      <HeroSection>
        <Title>Core Tokenizer</Title>
        <Subtitle>
          The Ultimate Real-World Asset Tokenization Platform. Transform any physical or digital asset 
          into blockchain tokens with full regulatory compliance and institutional-grade security.
        </Subtitle>
        <HeroFeatures>
          <HeroFeature>
            <FiShield className="icon" />
            Core Blockchain Native
          </HeroFeature>
          <HeroFeature>
            <FiGlobe className="icon" />
            Global Compliance
          </HeroFeature>
          <HeroFeature>
            <FiLayers className="icon" />
            Multi-Token Standards
          </HeroFeature>
          <HeroFeature>
            <FiZap className="icon" />
            Instant Settlement
          </HeroFeature>
        </HeroFeatures>
        <CTAButton to="/tokenization">
          <FiLayers />
          Start Tokenizing Assets
        </CTAButton>
      </HeroSection>

      <StatsGrid>
        <StatCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <FiDatabase className="stat-icon" />
          <div className="stat-value">
            {loading && assets.length === 0 ? '…' : statistics.totalAssets.toLocaleString()}
          </div>
          <div className="stat-label">Total Assets Registered</div>
          <div className="stat-meta">From the assets API (your visible scope)</div>
        </StatCard>

        <StatCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <FiAward className="stat-icon" />
          <div className="stat-value">
            {loading && assets.length === 0 ? '…' : formatUsdCompact(statistics.totalValue)}
          </div>
          <div className="stat-label">Total Assets Value</div>
          <div className="stat-meta">Sum of valuation (USD) on loaded assets</div>
        </StatCard>

        <StatCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <FiLayers className="stat-icon" />
          <div className="stat-value">
            {loading && assets.length === 0 ? '…' : statistics.activeTokenizations.toLocaleString()}
          </div>
          <div className="stat-label">Active Tokenizations</div>
          <div className="stat-meta">Minted assets (API) or active tokenizations in this session</div>
        </StatCard>

        <StatCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <FiPieChart className="stat-icon" />
          <div className="stat-value">
            {loading && assets.length === 0 ? '…' : statistics.fractionalizedAssets.toLocaleString()}
          </div>
          <div className="stat-label">Fractionalized Assets</div>
          <div className="stat-meta">Assets with status &quot;Fractionalized&quot;</div>
        </StatCard>
      </StatsGrid>

      {isAdmin && (
        <>
          <SectionTitle>
            <FiUsers className="icon" />
            Tokenizers & Reviewers
          </SectionTitle>
          <AdminDashboardCardsGrid>
            <AdminDashboardActionLink
              to="/authorized-tokenizers"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <FiUsers className="icon" />
              <div className="title">Authorized Tokenizers</div>
              <div className="description">
                Manage token issuers: create and update organizations, approve or suspend, and maintain operator
                accounts tied to each tokenizer.
              </div>
              <div className="features">
                <span className="feature-tag">Issuer registry</span>
                <span className="feature-tag">Approve & suspend</span>
                <span className="feature-tag">Operator accounts</span>
              </div>
            </AdminDashboardActionLink>
            <AdminDashboardActionLink
              to="/admin/legal-review-firms"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <FiBriefcase className="icon" />
              <div className="title">Legal Reviewers</div>
              <div className="description">
                Administer legal reviewer firms on the platform—the same scope as Legal reviewer firms in the header:
                directory, metadata, and how reviewers participate in asset workflows.
              </div>
              <div className="features">
                <span className="feature-tag">Reviewer firms</span>
                <span className="feature-tag">Platform directory</span>
                <span className="feature-tag">Legal workflow</span>
              </div>
            </AdminDashboardActionLink>
          </AdminDashboardCardsGrid>

          <SectionTitle>
            <FiShield className="icon" />
            Reviews
          </SectionTitle>
          <AdminDashboardCardsGrid>
            <AdminDashboardActionLink
              to="/legal-reviews"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <FiShield className="icon" />
              <div className="title">Assign to Review</div>
              <div className="description">
                Open the legal review center—the same destination as Legal reviews in the header: view the queue,
                assign reviewers, add comments, and approve or reject assets before minting.
              </div>
              <div className="features">
                <span className="feature-tag">Review queue</span>
                <span className="feature-tag">Assign reviewers</span>
                <span className="feature-tag">Approve & reject</span>
              </div>
            </AdminDashboardActionLink>
          </AdminDashboardCardsGrid>

          <SectionTitle>
            <FiSettings className="icon" />
            Asset Classes
          </SectionTitle>
          <AdminDashboardCardsGrid>
            <AdminDashboardActionLink
              to="/admin/asset-class-templates"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <FiLayers className="icon" />
              <div className="title">Asset class templates</div>
              <div className="description">
                Create new templates or clone existing ones. Catalog is stored in the database and appears in
                Asset Class Manager for authorized tokenizers.
              </div>
              <div className="features">
                <span className="feature-tag">Clone & create</span>
                <span className="feature-tag">Database catalog</span>
                <span className="feature-tag">Tokenizer visibility</span>
              </div>
            </AdminDashboardActionLink>
            <AdminDashboardActionLink
              to="/admin/asset-classes"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <FiDatabase className="icon" />
              <div className="title">Standard asset classes</div>
              <div className="description">
                Create shared asset classes from templates or custom schemas. They appear in Asset Class Manager for
                every authorized tokenizer, labeled as standard catalog entries.
              </div>
              <div className="features">
                <span className="feature-tag">Shared schemas</span>
                <span className="feature-tag">Template-based</span>
                <span className="feature-tag">Standard catalog</span>
              </div>
            </AdminDashboardActionLink>
          </AdminDashboardCardsGrid>

          <SectionTitle>
            <FiDatabase className="icon" />
            Assets
          </SectionTitle>
          <AdminDashboardCardsGrid>
            <AdminDashboardActionLink
              to="/admin/assets"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <FiDatabase className="icon" />
              <div className="title">Assets Registry</div>
              <div className="description">
                Full platform registry (same as Platform assets in the header): filter by tokenizer, sector, legal
                reviewer, status, and open contracts, mints, and documents.
              </div>
              <div className="features">
                <span className="feature-tag">Platform scope</span>
                <span className="feature-tag">Filters & search</span>
                <span className="feature-tag">Mints & documents</span>
              </div>
            </AdminDashboardActionLink>
            <AdminDashboardActionLink
              to="/admin/bulk-assets-registry"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <FiGrid className="icon" />
              <div className="title">Bulk Assets Registry</div>
              <div className="description">
                Admin view of bulk asset batches across the platform; align with platform assets and tokenize when
                members meet approval requirements.
              </div>
              <div className="features">
                <span className="feature-tag">Bulk batches</span>
                <span className="feature-tag">Approval gate</span>
                <span className="feature-tag">ERC-721</span>
              </div>
            </AdminDashboardActionLink>
          </AdminDashboardCardsGrid>

          <SectionTitle>
            <FiUserPlus className="icon" />
            Users
          </SectionTitle>
          <AdminDashboardCardsGrid>
            <AdminDashboardActionLink
              to="/admin/users"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <FiUserPlus className="icon" />
              <div className="title">User Management</div>
              <div className="description">
                Create and edit platform accounts, assign roles (admin, tokenizer operator, legal reviewer, auditor),
                link users to issuers and firms, suspend or reactivate access—same destination as User management in
                the header.
              </div>
              <div className="features">
                <span className="feature-tag">Roles & access</span>
                <span className="feature-tag">Issuer assignment</span>
                <span className="feature-tag">Suspend & reactivate</span>
              </div>
            </AdminDashboardActionLink>
          </AdminDashboardCardsGrid>

          <SectionTitle>
            <FiSend className="icon" />
            Transfers
          </SectionTitle>
          <AdminDashboardCardsGrid>
            <AdminDashboardActionLink
              to="/admin/fuel-injection"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <FiSend className="icon" />
              <div className="title">Fuel Injection</div>
              <div className="description">
                Send native XCB on Core Blockchain to multiple authorized tokenizers in one offline-signed batch file,
                then broadcast from the app.
              </div>
              <div className="features">
                <span className="feature-tag">Batch payouts</span>
                <span className="feature-tag">Offline signing</span>
                <span className="feature-tag">Broadcast</span>
              </div>
            </AdminDashboardActionLink>
          </AdminDashboardCardsGrid>
        </>
      )}

      {isLegalReviewer && !isAdmin && (
        <>
          <SectionTitle>
            <FiShield className="icon" />
            Reviews
          </SectionTitle>
          <AdminDashboardCardsGrid>
            <AdminDashboardActionLink
              to="/legal-reviews"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <FiShield className="icon" />
              <div className="title">Legal Reviews</div>
              <div className="description">
                Review and approve asset documentation before on-chain minting: open the legal review queue, assign
                cases, comment, and approve or reject.
              </div>
              <div className="features">
                <span className="feature-tag">Review queue</span>
                <span className="feature-tag">Assign & comment</span>
                <span className="feature-tag">Approve & reject</span>
              </div>
            </AdminDashboardActionLink>
          </AdminDashboardCardsGrid>
        </>
      )}

      <CategoriesSection>
        <SectionTitle>
          <FiTarget className="icon" />
          Asset Categories
        </SectionTitle>
        <CategoriesGrid>
          {Object.values(categories).map((category, index) => {
            const stats = categoryStats[category.id] || { assets: 0, value: 0 };
            return (
              <CategoryCard
                key={category.id}
                color={category.color}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.02 }}
              >
                <div className="category-header">
                  <span className="category-icon">{category.icon}</span>
                  <span className="category-name">{category.name}</span>
                </div>
                <div className="category-description">{category.description}</div>
                <div className="category-stats">
                  <div className="category-stat">
                    <span className="value">{stats.assets}</span>
                    <span className="label">Assets</span>
                  </div>
                  <div className="category-stat">
                    <span className="value">{formatUsdCompact(stats.value)}</span>
                    <span className="label">Value</span>
                  </div>
                  <div className="category-stat">
                    <span className="value">{stats.tokenized}</span>
                    <span className="label">Tokenized</span>
                  </div>
                </div>
              </CategoryCard>
            );
          })}
        </CategoriesGrid>
      </CategoriesSection>

      <ToolsSection>
        <SectionTitle>
          <FiGrid className="icon" />
          Tools & operations
        </SectionTitle>
        <QuickActionsGrid>
          {toolsAndOperationsItems.map(({ to, title, description, Icon }) => (
            <QuickActionCard key={to} to={to}>
              <Icon className="action-icon" />
              <div className="action-title">{title}</div>
              <div className="action-description">{description}</div>
            </QuickActionCard>
          ))}
        </QuickActionsGrid>
      </ToolsSection>

      <SectionTitle>
        <FiZap className="icon" />
        Quick Actions
      </SectionTitle>
      <QuickActionsGrid>
        <QuickActionCard to="/tokenization">
          <FiLayers className="action-icon" />
          <div className="action-title">Core Tokenizer</div>
          <div className="action-description">
            Access the complete RWA tokenization platform with all asset categories and token types
          </div>
        </QuickActionCard>
        
        <QuickActionCard to="/portfolio">
          <FiPieChart className="action-icon" />
          <div className="action-title">Portfolio Management</div>
          <div className="action-description">
            Monitor your tokenized asset portfolio with comprehensive analytics
          </div>
        </QuickActionCard>

        <QuickActionCard to="/legal-reviews">
          <FiShield className="action-icon" />
          <div className="action-title">Legal reviews</div>
          <div className="action-description">
            Review and approve asset documentation before on-chain minting
          </div>
        </QuickActionCard>
      </QuickActionsGrid>
    </DashboardContainer>
  );
}

export default Dashboard; 